import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { Button } from "~/shared/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/shared/ui/dialog";

import { useDeleteMessages } from "./useDeleteMessages";

interface DeleteMessagesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    chatId: string;
    workspaceId: string;
    messageIds: string[];
    // true, когда выбраны ВСЕ сообщения чата → после удаления он опустеет.
    willEmptyChat: boolean;
    // Успех — очищаем выбор в родителе и закрываем панель.
    onDeleted: () => void;
}

// Подтверждение жёсткого удаления импортированных сообщений (суперадмин):
// БД + S3 + billing (refund) — необратимо. Если выбраны все сообщения чата —
// второе окно (вложенный Radix-Dialog, тот же приём, что и в диалоге переноса):
// «Удалить чат» → удаление вместе с опустевшим чатом, «Оставить чат» →
// удалить только сообщения.
export function DeleteMessagesDialog({
    open,
    onOpenChange,
    chatId,
    workspaceId,
    messageIds,
    willEmptyChat,
    onDeleted,
}: DeleteMessagesDialogProps) {
    const { t } = useTranslation("chat");
    const navigate = useNavigate();
    const del = useDeleteMessages();

    // Второе окно: чат опустеет — удалить ли и его.
    const [confirmChatOpen, setConfirmChatOpen] = useState(false);

    // Каждое открытие — с чистого листа.
    useEffect(() => {
        if (open) {
            setConfirmChatOpen(false);
            del.reset();
        }
        // del — стабильный mutation-объект, намеренно не в deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const performDelete = async (deleteChatIfEmpty: boolean) => {
        try {
            const res = await del.mutateAsync({ chatId, workspaceId, messageIds, deleteChatIfEmpty });
            toast.success(t("deleteMessages.dialog.successDeleted", { count: res.deleted }));
            onDeleted();
            setConfirmChatOpen(false);
            onOpenChange(false);
            // Чат удалён (мы на его странице) → уходим на список чатов.
            if (res.chatDeleted) void navigate("/chat");
        } catch (error) {
            const fallback = t("deleteMessages.dialog.errorGeneric");
            toast.error(error instanceof Error && error.message ? error.message : fallback);
        }
    };

    const handleConfirm = async () => {
        if (del.isPending || messageIds.length === 0) return;
        // Все сообщения удаляются → сначала спрашиваем про удаление пустого чата.
        if (willEmptyChat) {
            setConfirmChatOpen(true);
            return;
        }
        await performDelete(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("deleteMessages.dialog.title")}</DialogTitle>
                    <DialogDescription>
                        {t("deleteMessages.dialog.description", { count: messageIds.length })}
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={del.isPending}
                    >
                        {t("deleteMessages.dialog.cancel")}
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={del.isPending || messageIds.length === 0}
                    >
                        {del.isPending
                            ? t("deleteMessages.dialog.submitting")
                            : t("deleteMessages.dialog.confirm")}
                    </Button>
                </DialogFooter>
            </DialogContent>

            {/* Второе окно: чат опустеет — удалить ли и сам чат (тот же приём, что в диалоге переноса). */}
            <Dialog open={confirmChatOpen} onOpenChange={setConfirmChatOpen}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t("deleteMessages.deleteEmpty.title")}</DialogTitle>
                        <DialogDescription>{t("deleteMessages.deleteEmpty.description")}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => performDelete(false)}
                            disabled={del.isPending}
                        >
                            {t("deleteMessages.deleteEmpty.keepChat")}
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => performDelete(true)}
                            disabled={del.isPending}
                        >
                            {t("deleteMessages.deleteEmpty.confirm")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    );
}
